import React, {
  ComponentProps,
  CSSProperties,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Popover from "antd/es/popover";
import Paragraph from "antd/es/typography/Paragraph";
import { Editor, Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";
import { RenderElementProps } from "slate-react/dist/components/editable";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import Button from "../../../../../Button";
import { useConfigProvider } from "../../../../../ConfigProvider";
import TextField from "../../../../../input/TextField";
import Modal from "../../../../../Modal";
import { ImageElement } from "../../../../../type/slate-types";

import styles from "./Image.module.scss";
import ImageLinkModal from "./ImageLinkModal";

type ResizeMode = "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
type ResizeParams = [number, number, number, number];

const RESIZER_OFFSET = 3;
const RESIZE_MODES: ResizeMode[] = ["bottomLeft", "bottomRight", "topLeft", "topRight"];

const getResizerStyle = (
  mode: ResizeMode,
  [resizerTop, resizerLeft, resizerRight, resizerBottom]: ResizeParams
): CSSProperties => {
  let result: CSSProperties;
  switch (mode) {
    case "bottomLeft":
      result = {
        bottom: resizerBottom - RESIZER_OFFSET,
        cursor: "nesw-resize",
        left: resizerLeft - RESIZER_OFFSET,
      };
      break;
    case "bottomRight":
      result = {
        bottom: resizerBottom - RESIZER_OFFSET,
        cursor: "nwse-resize",
        right: resizerRight - RESIZER_OFFSET,
      };
      break;
    case "topLeft":
      result = {
        cursor: "nwse-resize",
        left: resizerLeft - RESIZER_OFFSET,
        top: resizerTop - RESIZER_OFFSET,
      };
      break;
    case "topRight":
      result = {
        cursor: "nesw-resize",
        right: resizerRight - RESIZER_OFFSET,
        top: resizerTop - RESIZER_OFFSET,
      };
      break;
  }
  return result;
};

interface ImageProps {
  attributes: RenderElementProps["attributes"];
  children: React.ReactNode;
  element: ImageElement;
  readOnly?: boolean;
}

const Image = ({ attributes, element, readOnly }: ImageProps) => {
  const editor = useSlate();
  const configProvider = useConfigProvider();

  const containerRef = useRef<HTMLDivElement>(null);

  const [focused, setFocused] = useState(false);
  const [[resizerTop, resizerLeft, resizerRight, resizerBottom], setResizerParams] =
    useState<ResizeParams>([0, 0, 0, 0]);
  const [[originalWidth, originalHeight], setOriginalSize] = useState<[number, number]>([0, 0]);
  const [[width, height], setSize] = useState<[number, number]>([0, 0]);
  const [ratio, setRatio] = useState(1);
  const [altText, setAltText] = useState(element.alt ?? "");
  const [url, setUrl] = useState(element.url);
  const [altEditText, setAltEditText] = useState(element.alt ?? "");
  const [altModalVisible, showAltModal, hideAltModal] = useBoolean();
  const [linkModalVisible, showLinkModal, hideLinkModal] = useBoolean();
  const [resizeMode, setResizeMode] = useState<
    "bottomLeft" | "bottomRight" | "topLeft" | "topRight" | undefined
  >();

  const path = ReactEditor.findPath(editor, element);

  const handleApplyNewSize = useCallback(
    (size: [number, number]) => {
      setSize(size);
      Transforms.setNodes(editor, { width: size[0] }, { at: path });
    },
    [editor, path]
  );

  const [bestFitWidth, bestFitHeight] = useMemo(() => {
    let width = originalWidth;
    let height = originalHeight;
    if (height >= 350) {
      height = 350;
      width = Math.round(height * ratio);
    }
    if (width >= 600) {
      width = 600;
      height = Math.round(width / ratio);
    }
    return [width, height];
  }, [originalWidth, originalHeight, ratio]);

  const [smallWidth, smallHeight] = useMemo(() => {
    return [Math.round(bestFitWidth / 2), Math.round(bestFitHeight / 2)];
  }, [bestFitWidth, bestFitHeight]);

  const isBestFitSizeApplied = height === bestFitHeight && width === bestFitWidth;
  const isSmallSizeApplied = height === smallHeight && width === smallWidth;
  const isOriginalSizeApplied = height === originalHeight && width === originalWidth;

  const applyOriginalSize = useCallback(
    () => handleApplyNewSize([originalWidth, originalHeight]),
    [handleApplyNewSize, originalWidth, originalHeight]
  );

  const applyBestFitSize = useCallback(
    () => handleApplyNewSize([bestFitWidth, bestFitHeight]),
    [handleApplyNewSize, bestFitWidth, bestFitHeight]
  );

  const applySmallSize = useCallback(
    () => handleApplyNewSize([smallWidth, smallHeight]),
    [handleApplyNewSize, smallWidth, smallHeight]
  );

  const handleAltConfirm = useCallback(() => {
    setAltText(altEditText);
    Transforms.setNodes(editor, { alt: altEditText }, { at: path });
    hideAltModal();
  }, [altEditText, editor, hideAltModal, path, setAltText]);

  const handleChangeAltClick = useCallback(() => {
    setAltEditText(altText);
    showAltModal();
  }, [altText, setAltEditText, showAltModal]);

  const handleSaveLink = useCallback(
    (url: string) => {
      setUrl(url);
      Transforms.setNodes(editor, { url }, { at: path });
      hideLinkModal();
    },
    [editor, setUrl, path, hideLinkModal]
  );

  const handleRemoveLink = useCallback(() => {
    setUrl(undefined);
    Transforms.setNodes(editor, { url: undefined }, { at: path });
  }, [editor, setUrl, path]);

  const handleLoad = useCallback<NonNullable<ComponentProps<"img">["onLoad"]>>(
    (e) => {
      const { naturalHeight, naturalWidth } = e.target as HTMLImageElement;
      const ratio = naturalWidth / naturalHeight;
      const width = element.width ?? naturalWidth;
      const height = Math.round(width / ratio);
      setSize([width, height]);
      setOriginalSize([naturalWidth, naturalHeight]);
      setRatio(ratio);
    },
    [element, setRatio, setSize, setOriginalSize]
  );

  const handleClick = useCallback<MouseEventHandler>(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!readOnly && !focused) {
        const nextPath = Editor.after(editor, path);
        if (nextPath) {
          Transforms.select(editor, nextPath);
          ReactEditor.focus(editor);
        }
        setFocused(true);
      }
    },
    [editor, focused, path, readOnly]
  );

  const handleMouseMove = useDynamicCallback(({ x, y }: MouseEvent) => {
    if (containerRef.current && resizeMode && height !== 0) {
      const rect = containerRef.current.getBoundingClientRect();
      let newWidth = ["bottomRight", "topRight"].includes(resizeMode)
        ? x - rect.left
        : rect.left + width - x;
      let newHeight = ["bottomLeft", "bottomRight"].includes(resizeMode)
        ? y - rect.top
        : rect.top + height - y;

      if (newWidth / newHeight > ratio) {
        newWidth = newHeight * ratio;
      } else {
        newHeight = newWidth / ratio;
      }
      const widthDiff = width - newWidth;
      const heightDiff = height - newHeight;
      const top = ["topLeft", "topRight"].includes(resizeMode) ? heightDiff : 0;
      const bottom = ["bottomLeft", "bottomRight"].includes(resizeMode) ? heightDiff : 0;
      const left = ["bottomLeft", "topLeft"].includes(resizeMode) ? widthDiff : 0;
      const right = ["bottomRight", "topRight"].includes(resizeMode) ? widthDiff : 0;

      setResizerParams([top, left, right, bottom]);
    }
  });

  const handleMouseUp = useDynamicCallback(() => {
    if (resizeMode) {
      handleApplyNewSize([width - resizerLeft - resizerRight, height - resizerTop - resizerBottom]);
      setResizerParams([0, 0, 0, 0]);
      setResizeMode(undefined);
    }
  });

  const handleKeyDown = useDynamicCallback((event: KeyboardEvent) => {
    if (focused && !linkModalVisible && !altModalVisible && event.key === "Delete") {
      handleDelete();
    }
  });

  const handleDelete = useDynamicCallback(() => {
    Transforms.removeNodes(editor, { at: path });
  });

  // using native popover implementation is closing popover when size is reducing
  const handleClickOutside = useDynamicCallback((event: MouseEvent) => {
    if (
      focused &&
      containerRef.current &&
      !containerRef.current.contains(event.target as Node) &&
      !linkModalVisible &&
      !altModalVisible
    ) {
      // Check if click is inside the popover
      const target = event.target as HTMLElement;
      const popoverElement = target.closest(`.${styles.popover}`);
      if (!popoverElement) {
        setFocused(false);
      }
    }
  });

  useEffect(() => {
    if (readOnly) {
      return;
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mouseup", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleKeyDown, handleMouseMove, handleMouseUp, handleClickOutside, readOnly]);

  useEffect(() => {
    if (focused && (linkModalVisible || altModalVisible)) {
      setFocused(false);
    }
  }, [focused, linkModalVisible, altModalVisible]);

  const renderToolbar = () => {
    return (
      <div className={styles.toolbar}>
        <div className={styles.toolbarRow}>
          <Button disabled={isSmallSizeApplied} onClick={applySmallSize} size="small" type="link">
            {configProvider.formatMessage("ui.emailBody.imageModal.size.small")}
          </Button>
          <div className={styles.divider} />
          <Button
            disabled={isBestFitSizeApplied}
            onClick={applyBestFitSize}
            size="small"
            type="link"
          >
            {configProvider.formatMessage("ui.emailBody.imageModal.size.bestFit")}
          </Button>
          <div className={styles.divider} />
          <Button
            disabled={isOriginalSizeApplied}
            onClick={applyOriginalSize}
            size="small"
            type="link"
          >
            {configProvider.formatMessage("ui.emailBody.imageModal.size.original")}
          </Button>
          <div className={styles.divider} />
          <Button onClick={handleChangeAltClick} size="small" type="link">
            {configProvider.formatMessage("ui.emailBody.imageModal.editAltText")}
          </Button>
          <div className={styles.divider} />
          <Button
            className={styles.removeControl}
            danger
            onClick={handleDelete}
            size="small"
            type="link"
          >
            {configProvider.formatMessage("ui.emailBody.imageModal.remove")}
          </Button>
        </div>
        <div className={styles.toolbarRow}>
          {url ? (
            <>
              <span className={styles.linkContainer}>
                <span className={styles.linkLabel}>
                  {configProvider.formatMessage("ui.emailBody.imageModal.goToLink")}
                </span>
                <Paragraph
                  className={styles.linkUrl}
                  ellipsis={{
                    tooltip: {
                      placement: "bottom",
                      title: url,
                    },
                  }}
                >
                  <a href={url} rel="noopener noreferrer" target="_blank">
                    {url}
                  </a>
                </Paragraph>
              </span>
              <div className={styles.divider} />
              <Button onClick={showLinkModal} size="small" type="link">
                {configProvider.formatMessage("ui.emailBody.imageModal.change")}
              </Button>
              <div className={styles.divider} />
              <Button
                className={styles.removeControl}
                danger
                onClick={handleRemoveLink}
                size="small"
                type="link"
              >
                {configProvider.formatMessage("ui.emailBody.imageModal.remove")}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={showLinkModal} size="small" type="link">
                {configProvider.formatMessage("ui.emailBody.imageModal.addLink")}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <span {...attributes}>
      <Popover
        content={renderToolbar()}
        open={focused}
        overlayClassName={styles.popover}
        placement="bottom"
        showArrow={false}
        trigger={[]}
      >
        <span
          className={styles.container}
          contentEditable={false}
          onClick={handleClick}
          ref={containerRef}
        >
          <img
            alt={element.alt}
            className={styles.image}
            onLoad={handleLoad}
            src={element.src}
            style={{ height, width }}
          />
          {focused && (
            <React.Fragment>
              <span
                className={styles.resizer}
                style={{
                  bottom: resizerBottom,
                  left: resizerLeft,
                  right: resizerRight,
                  top: resizerTop,
                }}
              />
              {RESIZE_MODES.map((mode) => (
                <span
                  className={styles.resizeControl}
                  key={mode}
                  onMouseDown={() => {
                    setResizeMode(mode);
                  }}
                  style={getResizerStyle(mode, [
                    resizerTop,
                    resizerLeft,
                    resizerRight,
                    resizerBottom,
                  ])}
                />
              ))}
            </React.Fragment>
          )}
        </span>
      </Popover>
      <Modal
        cancelButtonProps={{ hidden: true }}
        okText={configProvider.formatMessage("ui.emailBody.imageModal.ok")}
        onCancel={hideAltModal}
        onOk={handleAltConfirm}
        open={altModalVisible}
        title={configProvider.formatMessage("ui.emailBody.imageModal.altTextTitle")}
      >
        <div className={styles.altModalContent}>
          <p className={styles.altModalCaption}>
            {configProvider.formatMessage("ui.emailBody.imageModal.altTextDescription")}
          </p>
          <TextField
            label={configProvider.formatMessage("ui.emailBody.imageModal.description")}
            onChange={setAltEditText}
            value={altEditText}
          />
        </div>
      </Modal>
      {linkModalVisible && (
        <ImageLinkModal initialUrl={url} onCancel={hideLinkModal} onOk={handleSaveLink} />
      )}
    </span>
  );
};

export default Image;
